import React, { useEffect, useState } from 'react';
import { statsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Target, Crown, Medal } from 'lucide-react';
import { getInitials, cn } from '@/lib/utils';

const MEDAL_STYLES = [
  { color: 'text-amber-500', label: 'Oro', ring: 'ring-amber-400/40' },
  { color: 'text-slate-400', label: 'Argento', ring: 'ring-slate-400/40' },
  { color: 'text-orange-600', label: 'Bronzo', ring: 'ring-orange-500/40' },
];

// kind="mvp": quante volte primo nell'elezione MVP + voti ricevuti in tutto (solo lega).
const MVP = {
  load: (id) => statsAPI.getLeagueMvp(id).then((res) => res.data?.mvp || []),
  Icon: Crown,
  iconClass: 'text-amber-500',
  title: 'Classifica MVP',
  description: 'Quante volte è stato il migliore in campo (conta solo il primo posto) e quanti voti MVP ha ricevuto in tutte le partite.',
  loading: 'Caricamento classifica MVP…',
  empty: 'Nessun voto MVP ancora.',
  value: (p) => `${p.mvp} MVP · ${p.votes} ${p.votes === 1 ? 'voto' : 'voti'}`,
};

/**
 * Classifica cannonieri (o MVP) riusabile per lega o competizione.
 * Props: scope = 'league' | 'competition', scopeId, kind = 'goals' | 'mvp'
 */
export const TopScorersTab = ({ scope = 'league', scopeId, kind = 'goals' }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!scopeId) return;
    setLoading(true);
    const call = kind === 'mvp'
      ? MVP.load(scopeId)
      : (scope === 'competition'
        ? statsAPI.getCompetitionTopScorers(scopeId)
        : statsAPI.getLeagueTopScorers(scopeId)
      ).then((res) => res.data?.top_scorers || []);
    call
      .then(setData)
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [scope, scopeId, kind]);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground text-sm">
          {kind === 'mvp' ? MVP.loading : 'Caricamento cannonieri…'}
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground text-sm">
          <Target className="h-8 w-8 mx-auto mb-2 opacity-40" />
          {kind === 'mvp'
            ? MVP.empty
            : 'Nessun gol registrato ancora. I gol vengono contati dagli eventi delle partite confermate.'}
        </CardContent>
      </Card>
    );
  }

  const podium = data.slice(0, 3);
  const rest = data.slice(3);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {kind === 'mvp'
            ? <MVP.Icon className={cn('h-5 w-5', MVP.iconClass)} />
            : <Target className="h-5 w-5 text-rose-600" />}
          {kind === 'mvp' ? MVP.title : 'Classifica cannonieri'}
        </CardTitle>
        <CardDescription>
          {kind === 'mvp'
            ? MVP.description
            : 'Gol + rigori sulle partite confermate o completate (le autoreti non contano).'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Podio top-3 */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {[1, 0, 2].map((slot) => {
            const p = podium[slot];
            if (!p) return <div key={slot} />;
            const style = MEDAL_STYLES[slot];
            const isFirst = slot === 0;
            return (
              <div
                key={p.user_id}
                className={cn(
                  'flex flex-col items-center text-center rounded-xl border bg-card p-3',
                  isFirst ? 'ring-2 ' + style.ring : '',
                  slot === 1 ? '-mt-2 sm:-mt-4' : '',
                  slot === 2 ? 'mt-2 sm:mt-4' : '',
                )}
              >
                <div className="relative">
                  <Avatar className={cn(
                    'mb-2',
                    isFirst ? 'h-16 w-16' : 'h-12 w-12',
                  )}>
                    <AvatarImage src={p.picture} />
                    <AvatarFallback>{getInitials(p.name)}</AvatarFallback>
                  </Avatar>
                  {isFirst
                    ? <Crown className="absolute -top-2 -right-2 h-5 w-5 text-amber-500 fill-amber-500" />
                    : <Medal className={cn('absolute -top-1 -right-1 h-4 w-4 fill-current', style.color)} />}
                </div>
                <p className="text-sm font-semibold truncate w-full">{p.name}</p>
                {p.nickname && (
                  <p className="text-[10px] text-primary italic truncate w-full">«{p.nickname}»</p>
                )}
                <Badge variant="secondary" className="mt-1.5 font-mono">
                  {kind === 'mvp' ? MVP.value(p) : `${p.goals} gol`}
                </Badge>
              </div>
            );
          })}
        </div>

        {/* Resto in lista */}
        {rest.length > 0 && (
          <div className="border rounded-lg divide-y">
            {rest.map((p, i) => (
              <div key={p.user_id} className="flex items-center gap-3 px-3 py-2">
                <span className="text-xs font-mono text-muted-foreground w-6 text-right">{i + 4}.</span>
                <Avatar className="h-8 w-8">
                  <AvatarImage src={p.picture} />
                  <AvatarFallback>{getInitials(p.name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{p.name}</p>
                  {p.nickname && (
                    <p className="text-[11px] text-primary italic truncate">«{p.nickname}»</p>
                  )}
                </div>
                <Badge variant="secondary" className="font-mono">
                  {kind === 'mvp' ? MVP.value(p) : p.goals}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TopScorersTab;
